import { Component, ElementRef, EventEmitter, OnDestroy, Output, ViewChild, effect, inject, input, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { TtsService } from '../../services/tts.service';
import { AudioSpeed, TTSRequest } from '../../../../models/tts-models';
import { DrawerModule } from 'primeng/drawer';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { SliderModule } from 'primeng/slider';
import { SelectModule } from 'primeng/select';
import { InputTextModule } from 'primeng/inputtext';
import { VoiceTableSelectorComponent } from '../voice-table-selector/voice-table-selector.component';
import { TagModule } from 'primeng/tag';

import { CloudStorageData, MultiObjectStorageService, ObjectStorageData } from '@dataclouder/ngx-cloud-storage';
import { VoiceSelectorComponent } from '../google-voice-selector/google-voice-selector';
// import { AudioEffectService, AudioEffectType, AUDIO_EFFECT_TYPES } from '../../audio-effect.service';
import { AudioEffect, AudioEffectType } from '../../classes/audio-effects';

const AUDIO_EFFECT_TYPES: AudioEffectType[] = [
  'none',
  'echo',
  'reverb',
  'distortion',
  'telephone',
  'tremolo',
  'deepVoice',
  'highPitchVoice',
  'robot',
  'monster',
  'walkieTalkie',
];

export interface TTSGenerated {
  transcription: any;
  text: string;
  voice: string;
  generateTranscription: boolean;
  speedRate: number;
  speed: AudioSpeed;
  storage?: CloudStorageData; // Optional path for storage
}

@Component({
  selector: 'tts-playground',
  templateUrl: './tts-playground.html',
  styleUrls: ['./tts-playground.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DrawerModule,
    VoiceTableSelectorComponent,
    DialogModule,
    ButtonModule,
    SliderModule,
    SelectModule,
    InputTextModule,
    TagModule,
    VoiceSelectorComponent,
  ],
})
export class TTSPlayground implements OnDestroy {
  @Output() ttsGenerated = new EventEmitter<TTSGenerated>();
  @ViewChild('audioPlayer') audioPlayerRef: ElementRef<HTMLAudioElement> | undefined;

  path = input<string>('');
  internalPath = signal<string>('');

  private readonly ttsService = inject(TtsService);
  private readonly storageService = inject(MultiObjectStorageService);

  isVoicesVisible = signal(false);

  // UI state properties
  message = signal<string>('');
  selectedVoice = signal<string>('default');
  speedRate = signal<number>(1.0);
  selectedSpeed = signal<AudioSpeed>(AudioSpeed.Regular);
  isGenerating = signal<boolean>(false);
  audioUrl = signal<string | null>(null);
  selectedEffect = signal<AudioEffectType>('none');
  isPlayingEffect = signal(false);

  private audioEffect: AudioEffect | null = null;
  private isAudioEffectInitialized = false;

  speedOptions = Object.values(AudioSpeed).map((speed) => ({ label: this.formatSpeedLabel(speed), value: speed }));
  effectOptions = AUDIO_EFFECT_TYPES.map((effect) => ({
    label: this.formatEffectLabel(effect),
    value: effect,
  }));

  constructor() {
    effect(() => {
      this.internalPath.set(this.path());
    });
  }

  async generateAudio(): Promise<void> {
    const text = this.message();
    if (!text) {
      console.warn('No text provided to speak.');
      return;
    }

    this.isGenerating.set(true);

    try {
      const ttsRequest: TTSRequest = {
        text: text,
        voice: this.selectedVoice(),
        generateTranscription: true,
        speedRate: this.speedRate(),
        speed: this.selectedSpeed(),
      };

      const audioData = await this.ttsService.getTextAudioFile(ttsRequest);

      let storageData: ObjectStorageData | null = null;
      const storagePath = this.internalPath();
      if (storagePath) {
        storageData = await this.storageService.uploadObject(audioData.blob, storagePath);
        console.log('Storage data:', storageData);
      }

      const generatedEvent: TTSGenerated = {
        transcription: audioData.transcription,
        text: ttsRequest.text,
        voice: ttsRequest.voice,
        generateTranscription: ttsRequest.generateTranscription,
        speedRate: ttsRequest.speedRate,
        speed: ttsRequest.speed,
        storage: storageData ?? undefined,
      };

      this.ttsGenerated.emit(generatedEvent);

      if (audioData.blobUrl) {
        this.audioUrl.set(audioData.blobUrl);
        this.isAudioEffectInitialized = false; // Reset on new audio
      }

      if (audioData.transcription) {
        console.log('Transcription:', audioData.transcription);
      }
    } catch (error) {
      console.error('TTS synthesis failed:', error);
    } finally {
      this.isGenerating.set(false);
    }
  }

  private async initializeAudioEffect(): Promise<void> {
    if (this.isAudioEffectInitialized) return;

    const audioPlayer = this.audioPlayerRef?.nativeElement;
    if (audioPlayer) {
      this.audioEffect = new AudioEffect();
      this.audioEffect.initialize(audioPlayer);
      audioPlayer.onended = () => this.isPlayingEffect.set(false);
      this.isAudioEffectInitialized = true;
    } else {
      console.error('Audio player element not found.');
    }
  }

  async playAudio(): Promise<void> {
    await this.initializeAudioEffect();
    if (!this.audioEffect) return;

    this.audioEffect.applyEffect('none');
    await this.audioEffect.play();
  }

  async playWithEffect(): Promise<void> {
    await this.initializeAudioEffect();
    if (!this.audioEffect) return;

    this.isPlayingEffect.set(true);
    try {
      this.audioEffect.applyEffect(this.selectedEffect());
      await this.audioEffect.play();
    } catch (error) {
      console.error('Failed to play with effect:', error);
      this.isPlayingEffect.set(false);
    }
  }

  handleVoiceSelected(voice: string): void {
    this.selectedVoice.set(voice);
    this.isVoicesVisible.set(false);
  }

  toggleVoicesPanel(): void {
    this.isVoicesVisible.update((visible) => !visible);
  }

  ngOnDestroy(): void {
    const url = this.audioUrl();
    if (url) {
      URL.revokeObjectURL(url);
    }
    this.audioEffect?.cleanup();
  }

  private formatEffectLabel(effect: AudioEffectType): string {
    return effect.charAt(0).toUpperCase() + effect.slice(1).replace(/([A-Z])/g, ' $1');
  }

  private formatSpeedLabel(speed: AudioSpeed): string {
    switch (speed) {
      case AudioSpeed.VerySlow:
        return 'Very Slow';
      case AudioSpeed.Slow:
        return 'Slow';
      case AudioSpeed.Regular:
        return 'Regular';
      case AudioSpeed.Fast:
        return 'Fast';
      case AudioSpeed.VeryFast:
        return 'Very Fast';
      default:
        return speed;
    }
  }
}
